import { faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { CGStockOptionsRSUsDetailsForm } from ".";
import { IFormITR1, IReviewCapitalGains, IStockOptionsRSUsForm } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const CGStockOptionsRSUsDetails: React.FC = () => {
	const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<IStockOptionsRSUsForm | null>(null);
	const [activeItem, setActiveItem] = useState<IStockOptionsRSUsForm | null>(null);

	// used in both edit and delete cases
	const [activeItemIdx, setActiveItemIdx] = useState<string>("");

	const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
	const StockOptionsRSUsDestails = cloneDeep(values.IncomeDetails?.StockOptionsRSUs || []);

	const handleDeleteInterestItem = async () => {
		StockOptionsRSUsDestails.splice(Number(activeItemIdx), 1);
		setFieldValue("IncomeDetails.StockOptionsRSUs", StockOptionsRSUsDestails);
		setShowDeleteModal(() => null);
		setActiveItem(() => null);
		setActiveItemIdx(() => "");
		handleTotalCalculation();
	};

	const handleTotalCalculation = () => {
		const StockOptionsRSUsGains =
			StockOptionsRSUsDestails?.reduce((acc, curr) => {
				const value = parseInt(curr.Gains);
				return acc + (isNaN(value) ? 0 : value);
			}, 0) || 0;

		const CapitalGains: IReviewCapitalGains = {
			...values.CapitalGains,
			TotalStockOptionsRSUsGain: StockOptionsRSUsGains
		};

		const TotalCapitalGains = Object.values(CapitalGains)?.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

		const DATA = {
			CapitalGains: CapitalGains,
			TotalCapitalGains: TotalCapitalGains
		};

		setValues({ ...cloneDeep(values), ...DATA });
		setFieldValue("IncomeDetails.StockOptionsRSUs", StockOptionsRSUsDestails);
		setFieldValue("LastSavedTimeStamp", new Date().getTime());
	};

	return (
		<>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>Stock Options and RSUs</h5>
					<PreventEditComponent>
						<Button variant="secondary" size="sm" onClick={() => setShowAddFormCanvas(() => true)}>
							<FASvgIcon icon={faPlus} />
							Add
						</Button>
					</PreventEditComponent>
				</div>

				<dl className="eds-list eds-dl eds-inline-between">
					{StockOptionsRSUsDestails.map((stockOptionsRSUs, index) => {
						return (
							<div key={`any-asset-${index}`} className="eds-list--item">
								<dt>
									{stockOptionsRSUs.DescriptionAssetSold || stockOptionsRSUs.DescriptionOfSale}
								</dt>
								<dd>
									<span>
										<PriceDisplay price={stockOptionsRSUs?.Gains} />
									</span>
									<Button
										variant="link"
										onClick={() => {
											setShowAddFormCanvas(() => true);
											setActiveItem(() => stockOptionsRSUs);
											setActiveItemIdx(() => String(index));
										}}
									>
										View
										<PreventEditComponent>
											<>/Edit</>
										</PreventEditComponent>
									</Button>
									<PreventEditComponent>
										<Button
											variant="transparent"
											className="icon-only"
											onClick={() => {
												setShowDeleteModal(() => stockOptionsRSUs);
												setActiveItemIdx(() => String(index));
											}}
										>
											<FASvgIcon icon={faTrash} />
										</Button>
									</PreventEditComponent>
								</dd>
							</div>
						);
					})}
				</dl>
			</section>
			<CGStockOptionsRSUsDetailsForm
				showCanvas={showAddFormCanvas}
				handleCloseBtnPress={(stockOptionsRSUsData) => {
					if (stockOptionsRSUsData) {
						if (activeItem) {
							StockOptionsRSUsDestails.splice(Number(activeItemIdx), 1, stockOptionsRSUsData);
						} else {
							StockOptionsRSUsDestails.push(stockOptionsRSUsData);
						}
						handleTotalCalculation();
					}
					setShowAddFormCanvas(() => false);
					setActiveItem(() => null);
					setActiveItemIdx(() => "");
				}}
				formValues={activeItem}
			/>

			<ConfirmationModal
				showModal={!!showDeleteModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={<p>You want to delete this</p>}
				onYesBtnClick={() => handleDeleteInterestItem()}
				onNoBtnClick={() => setShowDeleteModal(() => null)}
				icon={faTrashAlt}
			/>
		</>
	);
};

export default CGStockOptionsRSUsDetails;
